/**
 * `/trainer-performance` presentation logic — how each headline number is
 * shown, how a change since last month is worked out and coloured, and the
 * "at a glance" lists against the team median. Pure: the page only renders
 * what these return. Every definition BEHIND the numbers is the report's
 * (`packages/canonical-actions/src/trainer-performance/compute.ts`).
 *
 * The per-number presentation (labels, formats, the precision shown, the
 * change since last month) is `@layer/canonical-data/trainer-performance-metrics`
 * since 2026-10-23, shared with the monthly review PDF so the two can never
 * label or round a number differently. It is re-exported here unchanged.
 */

import type {
  TrainerPerformanceHeadline,
  TrainerPerformanceHeadlineKey,
  TrainerReviewSummary,
  TrainerReviewWhatsAppStatus,
} from "./api";
import { METRICS } from "@layer/canonical-data/trainer-performance-metrics";
import { TRAINER_REVIEW_QUESTIONS } from "@layer/canonical-data/trainer-review-questions";

export {
  changeSinceLastMonth,
  count,
  METRICS,
  money,
  pct,
  rating,
  roundTo,
  type Better,
  type Metric,
  type MonthChange,
} from "@layer/canonical-data/trainer-performance-metrics";

/** The team table's columns, in order. */
export const TEAM_COLUMNS: ReadonlyArray<TrainerPerformanceHeadlineKey> = [
  "delivered",
  "occupancy_pct",
  "active_clients",
  "retention_pct",
  "rating_average",
  "trial_conversion_pct_3m",
  "signups",
  "repeat_purchases",
  "postsession_pct",
  "consultation_pct",
  "pay_total_minor",
  "pay_to_revenue_pct",
];

// ---------------------------------------------------------------------------
// At a glance — the ratios well clear of the team median.
// ---------------------------------------------------------------------------

/** The ratios the "at a glance" lists compare with the team. */
export const GLANCE_KEYS: ReadonlyArray<TrainerPerformanceHeadlineKey> = [
  "occupancy_pct",
  "retention_pct",
  "rating_average",
  "trial_conversion_pct_3m",
  "postsession_pct",
  "consultation_pct",
];

/** How far from the median before it is worth saying (points, or rating steps). */
const GLANCE_MARGIN: Readonly<Partial<Record<TrainerPerformanceHeadlineKey, number>>> = {
  rating_average: 0.2,
};
const DEFAULT_GLANCE_MARGIN = 5;

export interface Glance {
  readonly above: ReadonlyArray<string>;
  readonly below: ReadonlyArray<string>;
}

/** One line per ratio at least its margin above (or below) the team median. */
export function glanceAgainstTeam(
  headline: TrainerPerformanceHeadline,
  median: TrainerPerformanceHeadline,
): Glance {
  const above: string[] = [];
  const below: string[] = [];
  for (const key of GLANCE_KEYS) {
    const v = headline[key];
    const m = median[key];
    if (v === null || m === null) continue;
    const margin = GLANCE_MARGIN[key] ?? DEFAULT_GLANCE_MARGIN;
    const metric = METRICS[key];
    const line = `${metric.label}: ${metric.format(v)} (team ${metric.format(m)})`;
    if (v >= m + margin) above.push(line);
    else if (v <= m - margin) below.push(line);
  }
  return { above, below };
}

// ---------------------------------------------------------------------------
// The "Monthly review" panel (trainer-review-lane, 2026-10-23): how far a
// review got, and its answers in words. The questions and their options are
// the lane's own (`@layer/canonical-data/trainer-review-questions`), so the
// panel prints exactly what the trainer tapped.
// ---------------------------------------------------------------------------

/** What a review's WhatsApp status reads as. */
export const REVIEW_WHATSAPP_LABELS: Readonly<Record<TrainerReviewWhatsAppStatus, string>> = {
  pending: "not sent yet",
  waiting_for_continue: "waiting for the Continue tap",
  expired: "Continue never tapped (expired)",
  not_possible: "not possible (window closed)",
  failed: "failed",
  sent: "sent",
  delivered: "delivered",
  read: "read",
};

const SKIP_LABELS: Readonly<Record<string, string>> = {
  owner: "the owner gets no review",
  no_sessions: "no sessions this month",
  no_contact: "no phone or email on file",
};

/**
 * One line on how far a review got: "Sent 3 Nov, 09:00 · WhatsApp: waiting
 * for the Continue tap · Email: sent", "No review — no sessions this month",
 * or "Not sent yet". `formatAt` renders a timestamp in the studio's zone.
 */
export function reviewStatusLine(
  review: TrainerReviewSummary | null,
  formatAt: (iso: string) => string,
): string {
  if (review === null || (review.status !== "skipped" && !review.has_scorecard)) {
    return "Not sent yet";
  }
  if (review.status === "skipped") {
    return `No review — ${SKIP_LABELS[review.skip_reason ?? ""] ?? review.skip_reason ?? "skipped"}`;
  }
  const parts: string[] = [
    review.sent_at !== null
      ? `Sent ${formatAt(review.sent_at)}`
      : review.status === "failed"
        ? "Not delivered"
        : "Sending",
  ];
  if (review.whatsapp_status !== null) {
    parts.push(`WhatsApp: ${REVIEW_WHATSAPP_LABELS[review.whatsapp_status]}`);
  }
  if (review.email_state !== null) {
    parts.push(
      `Email: ${review.email_state === "pending" ? "not sent yet" : review.email_state}`,
    );
  }
  return parts.join(" · ");
}

/**
 * 2026-10-24 (trainer-review-call-time) — one line on the review call, or
 * null before any time was offered: "Call: Tue 11 Nov, 12:30 — confirmed",
 * "Call: picked Tue 11 Nov, 12:30 — waiting for your confirmation", "Call: 6
 * times offered — waiting for their pick", "Call: no time left to offer —
 * arrange it with them directly".
 */
export function reviewCallLine(
  review: TrainerReviewSummary | null,
  formatAt: (iso: string) => string,
): string | null {
  const call = review?.call;
  if (call === undefined || call.state === null) return null;
  const at = call.starts_at === null ? "" : formatAt(call.starts_at);
  switch (call.state) {
    case "offered":
      return `Call: ${call.offer_count} time${call.offer_count === 1 ? "" : "s"} offered — waiting for their pick`;
    case "proposed":
      return `Call: picked ${at} — waiting for your confirmation`;
    case "confirmed":
      return call.confirmation_sent
        ? `Call: ${at} — confirmed`
        : `Call: ${at} — confirmed, telling the trainer`;
    case "reoffer_due":
      return "Call: offering other times";
    case "unarranged":
      return "Call: no time left to offer — arrange it with them directly";
  }
}

/** Each question with the answer tapped, or "—" while there is none. */
export function reviewAnswers(
  review: TrainerReviewSummary | null,
): ReadonlyArray<{ readonly question: string; readonly answer: string }> {
  return TRAINER_REVIEW_QUESTIONS.map((q) => {
    const raw = review?.answers[q.column] ?? null;
    const code = raw === null ? null : String(raw);
    const option = code === null ? null : q.options.find((o) => o.code === code);
    return { question: q.text, answer: option?.label ?? code ?? "—" };
  });
}
